import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatDistance } from "@/lib/api";
import { payoutMethodError } from "@/lib/payoutValidation";
import { StatusBadge } from "@/components/StatusBadge";
import { AttemptCard } from "@/components/AuditTrail";
import { SupplementalEvidenceList, attemptNeedsSupplemental } from "@/components/SupplementalEvidence";
import { DocumentVault } from "@/components/DocumentVault";
import { DocumentRequests } from "@/components/DocumentRequests";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FileImage } from "@/components/FileImage";
import { ReturnOfService } from "@/components/ReturnOfService";
import { ArrowLeft, MapPin, Camera, Navigation, Crosshair, FileSignature, Check, X, FileText, ShieldCheck, AlertTriangle, Wallet, CreditCard, PlusCircle } from "lucide-react";

function Detail({ label, value, testid }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</dt>
      <dd data-testid={testid} className="mt-0.5 text-sm font-medium text-slate-800">{value || "—"}</dd>
    </div>
  );
}

const NOT_SERVED_REASONS = [
  { v: "no_answer", l: "No answer" }, { v: "no_access", l: "No access" }, { v: "vacant", l: "Vacant" },
  { v: "need_unit", l: "Need unit number" }, { v: "no_such_address", l: "No such address" },
  { v: "evading", l: "Evading" }, { v: "access_barrier", l: "Access barrier" }, { v: "other", l: "Other (incl. refusal)" },
];
const ACCESS_BARRIER_SUBREASONS = [
  { v: "gated_locked", l: "Gated/locked entry" }, { v: "guard_refused", l: "Guard or management refused entry" },
  { v: "no_answer_secured", l: "No answer at secured door" }, { v: "other", l: "Other" },
];
const NO_ACCESS_BARRIERS = [
  { v: "gated_community", l: "Gated community" }, { v: "secured_building", l: "Secured building" },
  { v: "private_gate", l: "Private gate" }, { v: "animal", l: "Animal" }, { v: "other", l: "Other" },
];
const MEASURES = [
  { v: "waited", l: "Waited" }, { v: "asked_neighbors", l: "Asked neighbors" }, { v: "called", l: "Called" }, { v: "contacted_office", l: "Contacted office" },
];
const SPOKE_WITH = [
  { v: "none", l: "No one" }, { v: "occupant", l: "Occupant" }, { v: "neighbor", l: "Neighbor" },
  { v: "property_manager", l: "Property manager / landlord" }, { v: "on_site_worker", l: "On-site worker" },
  { v: "security", l: "Security" }, { v: "other", l: "Other" },
];
const SPOKE_STATEMENT_PERSONS = ["neighbor", "property_manager", "on_site_worker"];
const SPOKE_STATEMENTS = [
  { v: "says_resides", l: "Says subject resides" },
  { v: "says_not_resident", l: "Says not a resident" },
  { v: "doesnt_recognize_name", l: "Doesn't recognize name" },
];
const NARRATIVE_REQUIRED_REASONS = ["other", "evading"];
const _RLBL = Object.fromEntries(NOT_SERVED_REASONS.map((x) => [x.v, x.l]));
const _STMT_LBL = {
  says_resides: "who says the subject resides at the address",
  says_not_resident: "who says the subject is not a resident",
  doesnt_recognize_name: "who does not recognize the name",
};

function buildDeclaration({ outcome, reason, barrier, measures, spokeWith, spokeStatement, narrative, gps }) {
  const loc = gps ? "with GPS captured" : "without GPS (Unverified)";
  let t;
  if (outcome === "served") t = `I personally completed service at the service address, ${loc}.`;
  else {
    t = `I attempted service at the service address, ${loc}, and was unable to complete service (reason: ${(_RLBL[reason] || "unspecified").toLowerCase()}).`;
    if (reason === "no_access") {
      const bl = (NO_ACCESS_BARRIERS.find((x) => x.v === barrier) || {}).l || barrier || "a barrier";
      const ms = (measures || []).map((m) => (MEASURES.find((x) => x.v === m) || {}).l || m).join(", ");
      t += ` Access was blocked by ${bl.toLowerCase()}. Measures taken: ${ms}.`;
    }
    if (spokeWith) {
      const sw = (SPOKE_WITH.find((x) => x.v === spokeWith) || {}).l || spokeWith;
      let seg = ` I spoke with ${sw.toLowerCase()}`;
      if (spokeStatement && SPOKE_STATEMENT_PERSONS.includes(spokeWith)) seg += `, ${_STMT_LBL[spokeStatement] || ""}`;
      t += seg + ".";
    }
  }
  if ((narrative || "").trim()) t += " " + narrative.trim();
  return t;
}

export default function ServerJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  // Download the ARPS service package (cover page 1 + this order's client documents) using the
  // authenticated session, mirroring the working admin blob-download (window.open dropped the auth cookie).
  const downloadPackage = async () => {
    try {
      const resp = await api.get(`/orders/${id}/service-package`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `service_package_${order.job_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unable to download service package");
    }
  };

  const downloadCover = async () => {
    try {
      const resp = await api.get(`/orders/${id}/cover-letter`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `cover_${order.job_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Unable to download cover sheet");
    }
  };

  // attempt form
  const [gps, setGps] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [locConfirm, setLocConfirm] = useState(null);
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState("served");
  const [attemptOpen, setAttemptOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [barrier, setBarrier] = useState("");
  const [barrierSub, setBarrierSub] = useState("");
  const [measures, setMeasures] = useState([]);
  const [spokeWith, setSpokeWith] = useState("");
  const [spokeStatement, setSpokeStatement] = useState("");
  const [narrative, setNarrative] = useState("");
  const [declConfirmed, setDeclConfirmed] = useState(false);
  const [attemptTs, setAttemptTs] = useState(null);
  const [livePhotos, setLivePhotos] = useState([]);
  const [camOpen, setCamOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // supplemental location evidence
  const [suppFor, setSuppFor] = useState(null);
  const [suppGps, setSuppGps] = useState(null);
  const [suppCapturing, setSuppCapturing] = useState(false);
  const [suppPhoto, setSuppPhoto] = useState(null);
  const [suppAck, setSuppAck] = useState(false);
  const [suppNote, setSuppNote] = useState("");
  const [suppBusy, setSuppBusy] = useState(false);
  const [suppRefresh, setSuppRefresh] = useState(0);

  // affidavit
  const [affOpen, setAffOpen] = useState(false);
  const [signature, setSignature] = useState("");
  const [statement, setStatement] = useState("");
  const [printRequested, setPrintRequested] = useState(false);
  const [printPages, setPrintPages] = useState(1);

  // served fallback: photo/GPS unavailable → manual notarized affidavit
  const [unableCapture, setUnableCapture] = useState(false);
  const [unableReason, setUnableReason] = useState("");
  const [unableText, setUnableText] = useState("");
  const [manualScan, setManualScan] = useState(null);
  const [manualBusy, setManualBusy] = useState(false);

  // payout / banking
  const [payoutProfile, setPayoutProfile] = useState(undefined); // undefined = loading, null = none
  const [methodOpen, setMethodOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferType, setTransferType] = useState("expedited");
  const [selectedMethodId, setSelectedMethodId] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [payMethod, setPayMethod] = useState("debit_card");
  const [accountRef, setAccountRef] = useState("");
  const [bankRouting, setBankRouting] = useState("");
  const [methodErr, setMethodErr] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
    try {
      const { data: prof } = await api.get(`/server/payout-profile`);
      setPayoutProfile(prof);
    } catch { setPayoutProfile(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!order) return <p className="text-slate-400">Loading…</p>;
  const addr = order.service_address || {};

  const respond = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/respond?decision=${decision}`);
      toast.success(decision === "accept" ? "Job accepted" : "Job declined");
      if (decision === "decline") return nav("/server");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Take ownership of an ADMIN OFFER — unlocks documents. (Does not start work.)
  const acceptOffer = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/accept-offer`);
      toast.success("Assignment accepted — documents unlocked");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Self-claim an OPEN job (capped per server) — unlocks documents immediately.
  const claimJob = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/claim`);
      toast.success("Job claimed — documents unlocked");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const getPosition = (opts) => new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });

  const startJob = async () => {
    // HARD REQUIREMENT: a real browser-returned coordinate is required to start. No bypass.
    if (!navigator.geolocation) {
      toast.error("Location permission is required to start this job, but your device/browser does not support location. Enable location and use a supported browser.");
      return;
    }
    setBusy(true);
    let pos;
    try {
      // First: precise fix.
      pos = await getPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
    } catch (err) {
      // Permission denial is terminal; unavailable/timeout get one resilient low-accuracy retry.
      if (err && err.code === 1) {
        setBusy(false);
        toast.error("Location permission is required to start this job. Please enable location access in your browser/device settings and try again.");
        return;
      }
      try {
        // Fallback: lower accuracy + a reasonable cached position (desktops often lack GPS).
        pos = await getPosition({ enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 });
      } catch (err2) {
        setBusy(false);
        if (err2 && err2.code === 1) {
          toast.error("Location permission is required to start this job. Please enable location access in your browser/device settings and try again.");
        } else if (err2 && err2.code === 3) {
          toast.error("Location detection timed out. Please try again.");
        } else {
          toast.error("Your device could not determine your location. Please make sure location services are available and try again.");
        }
        return;
      }
    }
    // Guard: require actual finite coordinates before contacting the backend (no coords => blocked).
    if (!pos || typeof pos.coords?.latitude !== "number" || typeof pos.coords?.longitude !== "number"
        || Number.isNaN(pos.coords.latitude) || Number.isNaN(pos.coords.longitude)) {
      setBusy(false);
      toast.error("Your device could not determine your location. Please make sure location services are available and try again.");
      return;
    }
    try {
      // Only lat/lng/accuracy are sent; the backend independently re-enforces the location rule.
      await api.post(`/orders/${id}/respond?decision=accept`, { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
      toast.success("Location verified — job started");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startServiceAttempt = () => {
    // Start Service is the only way to open an attempt: capture GPS + timestamp at this moment.
    setGps(null); setPhotos([]); setLivePhotos([]); setNotes(""); setOutcome("served");
    setReason(""); setBarrier(""); setBarrierSub(""); setMeasures([]); setSpokeWith(""); setSpokeStatement(""); setNarrative("");
    setDeclConfirmed(false); setLocConfirm(null); setCamOpen(false);
    setAttemptTs(new Date().toISOString());
    setAttemptOpen(true);
    if (!navigator.geolocation) { toast.message("No GPS on this device — this attempt will be recorded as Unverified."); return; }
    setCapturing(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const g = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, timestamp: new Date().toISOString(), address: null, geocode_status: null };
        setGps(g); setCapturing(false);
        try {
          const { data } = await api.get("/geocode/reverse", { params: { lat: g.lat, lng: g.lng } });
          setGps((cur) => (cur ? { ...cur, address: data.address, geocode_status: data.status } : cur));
        } catch { setGps((cur) => (cur ? { ...cur, geocode_status: "failed" } : cur)); }
      },
      () => { setCapturing(false); toast.message("GPS unavailable — this attempt will be recorded as Unverified."); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const uploadOne = async (file, live) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPhotos((p) => [...p, data.id]);
    if (live) setLivePhotos((p) => [...p, data.id]);
    return data.id;
  };
  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    try { for (const f of files) await uploadOne(f, false); toast.message("Photo added (not live capture)"); }
    catch { toast.error("Upload failed"); }
  };
  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Live camera is not available on this device — use Upload from device."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream; setCamOpen(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); } }, 60);
    } catch { toast.error("Camera permission denied — use Upload from device (tagged not live)."); }
  };
  const closeCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCamOpen(false);
  };
  const capturePhoto = async () => {
    const v = videoRef.current; if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 640; canvas.height = v.videoHeight || 480;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob) { toast.error("Capture failed"); return; }
    try { await uploadOne(new File([blob], `attempt-${Date.now()}.jpg`, { type: "image/jpeg" }), true); toast.success("Live photo captured"); }
    catch { toast.error("Upload failed"); }
  };

  const hasLivePhoto = photos.some((pid) => livePhotos.includes(pid));
  const photoRequired = !(outcome === "served" && (!!gps || unableCapture));
  const photoReqMet = !photoRequired || hasLivePhoto;
  const declarationText = buildDeclaration({ outcome, reason, barrier, measures, spokeWith, spokeStatement, narrative, gps });
  const toggleMeasure = (v) => setMeasures((cur) => (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]));

  const downloadManualAffidavit = async () => {
    try {
      const res = await api.get(`/orders/${id}/manual-affidavit/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Could not generate the affidavit PDF"); }
  };
  const uploadManualScan = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setManualBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setManualScan({ id: data.id, name: data.filename });
      toast.success("Notarized scan uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setManualBusy(false); }
  };
  const submitManualAffidavit = async () => {
    if (!manualScan) { toast.error("Upload the signed, notarized affidavit scan first."); return; }
    setManualBusy(true);
    try {
      await api.post(`/orders/${id}/manual-affidavit`, { file_id: manualScan.id });
      toast.success("Manual affidavit submitted for admin review");
      setManualScan(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setManualBusy(false); }
  };
  const submitAttempt = async () => {
    if (!attemptTs) { toast.error("Tap Start Service attempt to capture GPS and time first."); return; }
    if (!locConfirm) { toast.error("Confirm whether you are physically at the service location."); return; }
    if (outcome === "served" && unableCapture) {
      if (!unableReason) { toast.error("Select why photo/GPS could not be captured."); return; }
      if (unableReason === "other" && !unableText.trim()) { toast.error("Describe why photo/GPS could not be captured."); return; }
    }
    if (outcome === "not_served") {
      if (!reason) { toast.error("Select a reason service was not completed."); return; }
      if (NARRATIVE_REQUIRED_REASONS.includes(reason) && !narrative.trim()) { toast.error("A narrative is required for this reason (Evading or Other/refusal)."); return; }
      if (reason === "no_access") {
        if (!barrier) { toast.error("Select the access barrier."); return; }
        if (!measures.length) { toast.error("Select at least one measure taken."); return; }
      }
      if (reason === "access_barrier") {
        if (!barrierSub) { toast.error("Select the access barrier."); return; }
        if (barrierSub === "other" && !narrative.trim()) { toast.error("Describe the access barrier (Other)."); return; }
        if (!measures.length) { toast.error("Select at least one measure taken."); return; }
      }
      if (!spokeWith) { toast.error("Select who you spoke with."); return; }
      if (SPOKE_STATEMENT_PERSONS.includes(spokeWith) && !spokeStatement) { toast.error("Select what the person stated."); return; }
    }
    if (photoRequired && !photos.length) { toast.error("At least one in-app photo is required for this attempt."); return; }
    if (photoRequired && !hasLivePhoto) {
      const proceed = window.confirm("No live camera photo was captured. This attempt will be flagged 'photo not live capture' and will not count or be payable until an admin overrides with a note. Submit anyway?");
      if (!proceed) return;
    }
    if (!declConfirmed) { toast.error("Confirm the declaration under penalty of perjury."); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${id}/attempts`, {
        gps: gps || {}, photos, photos_live: photos.filter((p) => livePhotos.includes(p)),
        device_timestamp: attemptTs, notes, outcome, location_confirmation: locConfirm,
        not_served_reason: outcome === "not_served" ? reason : null,
        no_access_barrier: reason === "no_access" ? barrier : null,
        access_barrier_subreason: reason === "access_barrier" ? barrierSub : null,
        measures_taken: (reason === "no_access" || reason === "access_barrier") ? measures : [],
        spoke_with: outcome === "not_served" ? spokeWith : null,
        spoke_with_statement: (outcome === "not_served" && SPOKE_STATEMENT_PERSONS.includes(spokeWith)) ? spokeStatement : null,
        narrative: narrative || null, declaration_confirmed: true,
        unable_to_capture: outcome === "served" && unableCapture,
        unable_reason: (outcome === "served" && unableCapture) ? unableReason : null,
        unable_reason_text: (outcome === "served" && unableCapture && unableReason === "other") ? unableText.trim() : null,
      });
      toast.success(unableCapture ? "Attempt logged — manual, notarized affidavit required" : (gps ? "Attempt logged with audit trail" : "Attempt logged as Unverified (no GPS)"));
      closeCamera();
      setGps(null); setPhotos([]); setLivePhotos([]); setNotes(""); setOutcome("served"); setLocConfirm(null);
      setReason(""); setBarrier(""); setBarrierSub(""); setMeasures([]); setSpokeWith(""); setSpokeStatement(""); setNarrative(""); setDeclConfirmed(false); setAttemptTs(null);
      setUnableCapture(false); setUnableReason(""); setUnableText("");
      setAttemptOpen(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openSupp = (a) => { setSuppFor(a); setSuppGps(null); setSuppPhoto(null); setSuppAck(false); setSuppNote(""); };
  const closeSupp = () => { setSuppFor(null); setSuppGps(null); setSuppPhoto(null); setSuppAck(false); setSuppNote(""); };

  const captureSuppGps = () => {
    if (!navigator.geolocation) { toast.error("Location is not available on this device."); return; }
    setSuppCapturing(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const g = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, timestamp: new Date().toISOString(), address: null, geocode_status: null };
        setSuppGps(g);
        setSuppCapturing(false);
        toast.success("GPS location captured");
        try {
          const { data } = await api.get("/geocode/reverse", { params: { lat: g.lat, lng: g.lng } });
          setSuppGps((cur) => (cur ? { ...cur, address: data.address, geocode_status: data.status } : cur));
        } catch { setSuppGps((cur) => (cur ? { ...cur, geocode_status: "failed" } : cur)); }
      },
      () => { setSuppCapturing(false); toast.error("Location permission denied. Enable location access to capture GPS."); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const uploadSuppPhoto = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSuppPhoto(data.id);
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); }
  };

  const submitSupplemental = async () => {
    if (!suppPhoto) { toast.error("A current photo is required."); return; }
    if (!suppAck) { toast.error("Please acknowledge this is new evidence captured now."); return; }
    setSuppBusy(true);
    try {
      await api.post(`/orders/${id}/attempts/${suppFor.id}/supplemental-evidence`,
        { photo_id: suppPhoto, gps: suppGps || {}, acknowledged: suppAck, note: suppNote });
      toast.success("Supplemental location evidence added");
      closeSupp();
      setSuppRefresh((n) => n + 1);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSuppBusy(false); }
  };

  const submitAffidavit = async () => {
    if (!signature.trim()) return toast.error("Signature required");
    setBusy(true);
    try {
      await api.post(`/orders/${id}/affidavit`, { signature, statement, print_requested: printRequested, print_pages: Number(printPages) || 1 });
      toast.success("Affidavit signed & submitted for review");
      setAffOpen(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const savePayoutProfile = async () => {
    if (!payeeName.trim()) { setMethodErr("Legal / payee name is required."); return; }
    const ve = payoutMethodError(payMethod, accountRef, bankRouting);
    if (ve) { setMethodErr(ve); return; }
    setMethodErr("");
    setBusy(true);
    try {
      const { data } = await api.post(`/server/payout-profile`, { payee_name: payeeName, method: payMethod, account_ref: accountRef, routing_ref: bankRouting });
      setPayoutProfile(data);
      setMethodOpen(false); setAccountRef(""); setBankRouting("");
      toast.success("Payout method saved");
    } catch (e) { setMethodErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmTransfer = async () => {
    const comp = order.compensation;
    if (!comp) return;
    if (transferType === "expedited" && !ackChecked) return toast.error("Please acknowledge the 5% expedited fee");
    if (!confirmChecked) return toast.error("Please confirm you are requesting payment");
    setBusy(true);
    try {
      await api.post(`/compensations/${comp.id}/initiate-payout`, { transfer_type: transferType, confirm: true, method_id: selectedMethodId || undefined });
      toast.success(transferType === "expedited" ? "Expedited transfer initiated — processing" : "Scheduled for the no-fee weekly cycle");
      setTransferOpen(false); setConfirmChecked(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const isMine = order.status !== "pending_assignment";
  const isOffered = order.assignment_status === "offered";
  const docsUnlocked = !isOffered; // an offered-but-not-accepted server can't see documents
  const canAttempt = ["in_progress", "not_served"].includes(order.status) && isMine;
  const hasServed = (order.attempts || []).some((a) => a.outcome === "served");
  const canAffidavit = hasServed && !order.affidavit && isMine;
  const manualAttempt = (order.attempts || []).find((a) => a.outcome === "served" && a.manual_affidavit_required);
  const manualAffNeeded = !!manualAttempt && isMine && !(order.affidavit && order.affidavit.sealed) && ["served", "in_progress", "not_served"].includes(order.status);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${addr.street}, ${addr.city}, ${addr.state} ${addr.zip}`)}`;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <button data-testid="back-button" onClick={() => nav("/server")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to assignments
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Job #{order.job_number}</h2>
            <StatusBadge status={order.status} pulse />
          </div>
          <p className="mt-1 text-slate-500">{order.service_type} · {order.defendant}</p>
          {docsUnlocked && (
          <div className="mt-3 flex flex-wrap gap-2">
          <Button
            data-testid="download-service-package-btn"
            variant="outline"
            onClick={downloadPackage}
          >
            <FileText className="mr-1.5 h-4 w-4" /> Download service package
          </Button>
          <Button
            data-testid="download-cover-letter-btn"
            variant="outline"
            onClick={downloadCover}
          >
            <FileText className="mr-1.5 h-4 w-4" /> Cover sheet only
          </Button>
          </div>
          )}
        </div>
      </div>

      {/* Case & service details */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="server-job-details">
        <h3 className="font-display text-base font-bold text-slate-900">Case & service details</h3>
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <Detail label="Defendant" value={order.defendant} testid="server-detail-defendant" />
          <Detail label="Case number" value={order.case_number} testid="server-detail-case-number" />
          <Detail label="Jurisdiction" value={order.jurisdiction} testid="server-detail-jurisdiction" />
          <Detail label="Plaintiff" value={order.plaintiff} testid="server-detail-plaintiff" />
          <Detail label="Service type" value={order.service_type} testid="server-detail-service-type" />
          <Detail label="Deadline" value={order.deadline ? new Date(order.deadline).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "No deadline specified"} testid="server-detail-deadline" />
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium uppercase tracking-wider text-slate-400">Instructions</dt>
            <dd data-testid="server-detail-instructions" className="mt-0.5 whitespace-pre-wrap text-sm text-slate-700">{order.instructions || "—"}</dd>
          </div>
        </dl>
      </div>

      {/* Authorized service windows (ARPS Service Standard — shared with client & Admin) */}
      {(order.service_windows || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="server-service-windows">
          <h3 className="font-display text-base font-bold text-slate-900">Authorized service windows</h3>
          <p className="mt-1 text-xs text-slate-500">Standard Service: up to {order.service_standard?.attempts || 4} diligent attempts. These are scheduling windows — record the actual date, time, and location of each attempt separately.</p>
          <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {order.service_windows.map((w) => (
              <li key={w.sequence || w.key} data-testid={`server-window-${w.key}`} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-600">{w.sequence ? `${w.sequence}. ` : ""}{w.label}</span>
                <span className="font-medium text-slate-900">{w.display}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-1.5 font-display text-base font-bold text-slate-900"><MapPin className="h-4 w-4 text-emerald-600" /> Service address</h3>
            <p className="mt-1 text-sm text-slate-700">{addr.street}, {addr.city}, {addr.state} {addr.zip}</p>
          </div>
          <a data-testid="navigate-button" href={mapsUrl} target="_blank" rel="noreferrer">
            <Button variant="outline"><Navigation className="mr-1.5 h-4 w-4" /> Navigate</Button>
          </a>
        </div>
      </div>

      {/* Start-location verification (captured GPS vs geocoded service address) */}
      {order.start_location && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="start-location-card">
          <h3 className="flex items-center gap-1.5 font-display text-base font-bold text-slate-900"><Crosshair className="h-4 w-4 text-slate-600" /> Start location verification</h3>
          <p className="mt-1 text-sm text-slate-600"><span className="font-semibold">Captured:</span> {order.start_location.address || `${Number(order.start_location.lat).toFixed(5)}, ${Number(order.start_location.lng).toFixed(5)}`}</p>
          {order.start_location.validation?.status && (() => {
            const v = order.start_location.validation;
            const cls = v.status === "pass" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : v.status === "flagged" ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200 bg-slate-50 text-slate-500";
            const label = v.status === "pass" ? "Location verified" : v.status === "flagged" ? "Outside expected service location" : "Proximity not validated";
            const Icon = v.status === "pass" ? ShieldCheck : v.status === "flagged" ? AlertTriangle : MapPin;
            return (
              <div className="mt-2" data-testid="start-location-proximity">
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}><Icon className="h-3.5 w-3.5" /> {label}</span>
                {v.distance_m != null && <p className="mt-1 text-xs text-slate-500">{formatDistance(v.distance_m)} from service address (allowed {formatDistance(v.allowed_m)})</p>}
                {v.status === "not_validated" && <p className="mt-1 text-xs text-slate-400">{v.reason}</p>}
              </div>
            );
          })()}
        </div>
      )}

      {/* Take ownership: claim an open job, or accept/decline an admin offer */}
      {order.status === "pending_assignment" && (
        <div className="flex gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <Button data-testid="claim-job-button" onClick={claimJob} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-1.5 h-4 w-4" /> Claim this job</Button>
        </div>
      )}
      {isOffered && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="assignment-offer-banner">
          <p className="text-sm font-medium text-amber-800">You've been offered this assignment. Accept to view the case documents.</p>
          <div className="flex gap-3">
            <Button data-testid="accept-offer-button" onClick={acceptOffer} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-1.5 h-4 w-4" /> Accept assignment</Button>
            <Button data-testid="decline-offer-button" onClick={() => respond("decline")} disabled={busy} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50"><X className="mr-1.5 h-4 w-4" /> Decline</Button>
          </div>
        </div>
      )}
      {order.status === "assigned" && isMine && !isOffered && (
        <div className="flex items-center justify-between rounded-xl border border-sky-200 bg-sky-50 p-4">
          <p className="text-sm font-medium text-sky-800">This job is yours. Start when you're ready to log attempts.</p>
          <Button data-testid="start-job-button" onClick={startJob} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800">Start job</Button>
        </div>
      )}

      {/* Action buttons */}
      {(canAttempt || canAffidavit) && (
        <div className="flex flex-wrap gap-3">
          {canAttempt && (
          <>
          <Button data-testid="new-attempt-button" onClick={startServiceAttempt} className="bg-emerald-600 text-white hover:bg-emerald-700"><Crosshair className="mr-1.5 h-4 w-4" /> Start Service attempt</Button>
          <Dialog open={attemptOpen} onOpenChange={(o) => { setAttemptOpen(o); if (!o) closeCamera(); }}>
            <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
              <DialogHeader><DialogTitle>Service attempt</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div data-testid="attempt-capture-status" className={`rounded-lg border px-3 py-2 text-xs ${gps ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                  {capturing ? "Capturing GPS…" : gps
                    ? <span className="font-mono-arps">GPS {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}{gps.accuracy != null ? ` ±${Math.round(gps.accuracy)}m` : ""}{gps.address ? ` · ${gps.address}` : ""}</span>
                    : "No GPS captured — this attempt will be recorded as UNVERIFIED."}
                </div>
                <div data-testid="location-confirmation">
                  <Label>Are you physically at the service location? <span className="text-red-500">*</span></Label>
                  <div className="mt-1.5 flex gap-2">
                    <Button type="button" variant="outline" data-testid="loc-confirm-yes" onClick={() => setLocConfirm("yes")} className={locConfirm === "yes" ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700" : ""}>YES</Button>
                    <Button type="button" variant="outline" data-testid="loc-confirm-no" onClick={() => setLocConfirm("no")} className={locConfirm === "no" ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800" : ""}>NO</Button>
                  </div>
                </div>
                <div>
                  <Label>Outcome</Label>
                  <Select value={outcome} onValueChange={(v) => { setOutcome(v); if (v === "served") setReason(""); }}>
                    <SelectTrigger data-testid="attempt-outcome-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="served">Served</SelectItem>
                      <SelectItem value="not_served">Not served</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {outcome === "served" && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="unable-capture-block">
                    <label className="flex items-start gap-2 text-sm font-medium text-slate-800">
                      <input type="checkbox" data-testid="unable-capture-toggle" checked={unableCapture} onChange={(e) => setUnableCapture(e.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300" />
                      Unable to capture photo/GPS (manual, notarized affidavit)
                    </label>
                    {unableCapture && (
                      <div className="space-y-2">
                        <Select value={unableReason} onValueChange={(v) => { setUnableReason(v); if (v !== "other") setUnableText(""); }}>
                          <SelectTrigger data-testid="unable-reason-select"><SelectValue placeholder="Select a reason *" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="no_signal">No signal</SelectItem>
                            <SelectItem value="device_failure">Device/camera failure</SelectItem>
                            <SelectItem value="app_issue">App issue</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        {unableReason === "other" && (
                          <Input data-testid="unable-reason-text" value={unableText} onChange={(e) => setUnableText(e.target.value)} placeholder="Describe the reason *" />
                        )}
                        <p className="text-xs text-amber-700">Photo/GPS won't be required. This served attempt stays Unverified until you upload a notarized affidavit that an admin approves.</p>
                      </div>
                    )}
                  </div>
                )}
                {outcome === "not_served" && (
                  <div>
                    <Label>Reason not served <span className="text-red-500">*</span></Label>
                    <Select value={reason} onValueChange={(v) => { setReason(v); if (v !== "no_access" && v !== "access_barrier") { setBarrier(""); setBarrierSub(""); setMeasures([]); setSpokeWith(""); } }}>
                      <SelectTrigger data-testid="attempt-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                      <SelectContent>{NOT_SERVED_REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {outcome === "not_served" && reason === "access_barrier" && (
                  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="access-barrier-fields">
                    <p className="text-xs text-amber-700">Saving this pauses the order for a client decision immediately (not only at the 4th attempt). It still counts as 1 of the 4 included attempts.</p>
                    <div>
                      <Label>Access barrier <span className="text-red-500">*</span></Label>
                      <Select value={barrierSub} onValueChange={setBarrierSub}>
                        <SelectTrigger data-testid="attempt-barriersub-select"><SelectValue placeholder="Select the barrier" /></SelectTrigger>
                        <SelectContent>{ACCESS_BARRIER_SUBREASONS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Measures taken <span className="text-red-500">*</span></Label>
                      <div className="mt-1 grid grid-cols-2 gap-1.5">
                        {MEASURES.map((m) => (
                          <label key={m.v} className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" data-testid={`ab-measure-${m.v}`} checked={measures.includes(m.v)} onChange={() => toggleMeasure(m.v)} className="h-4 w-4 rounded border-slate-300" />{m.l}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {outcome === "not_served" && reason === "no_access" && (
                  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="no-access-fields">
                    <div>
                      <Label>Barrier <span className="text-red-500">*</span></Label>
                      <Select value={barrier} onValueChange={setBarrier}>
                        <SelectTrigger data-testid="attempt-barrier-select"><SelectValue placeholder="Select a barrier" /></SelectTrigger>
                        <SelectContent>{NO_ACCESS_BARRIERS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Measures taken <span className="text-red-500">*</span></Label>
                      <div className="mt-1 grid grid-cols-2 gap-1.5">
                        {MEASURES.map((m) => (
                          <label key={m.v} className="flex items-center gap-2 text-sm text-slate-700">
                            <input type="checkbox" data-testid={`measure-${m.v}`} checked={measures.includes(m.v)} onChange={() => toggleMeasure(m.v)} className="h-4 w-4 rounded border-slate-300" />{m.l}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {outcome === "not_served" && (
                  <div className="grid gap-3 sm:grid-cols-2" data-testid="spoke-with-fields">
                    <div>
                      <Label>Who did you speak with? <span className="text-red-500">*</span></Label>
                      <Select value={spokeWith} onValueChange={(v) => { setSpokeWith(v); if (!SPOKE_STATEMENT_PERSONS.includes(v)) setSpokeStatement(""); }}>
                        <SelectTrigger data-testid="attempt-spoke-with-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{SPOKE_WITH.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {SPOKE_STATEMENT_PERSONS.includes(spokeWith) && (
                      <div>
                        <Label>Their statement <span className="text-red-500">*</span></Label>
                        <Select value={spokeStatement} onValueChange={setSpokeStatement}>
                          <SelectTrigger data-testid="attempt-spoke-statement-select"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{SPOKE_STATEMENTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
                {outcome === "not_served" && NARRATIVE_REQUIRED_REASONS.includes(reason) && (
                  <div>
                    <Label>Narrative <span className="text-red-500">*</span> <span className="text-xs font-normal text-slate-400">(required for {reason === "evading" ? "Evading" : "Other / refusal"})</span></Label>
                    <Textarea data-testid="attempt-narrative-input" value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={2} placeholder="Describe what happened…" />
                  </div>
                )}
                <div>
                  <Label>Photo evidence {photoRequired ? (photoReqMet ? <span className="text-xs font-normal text-emerald-600">(live photo captured)</span> : <span className="text-red-500">* live photo required</span>) : <span className="text-xs font-normal text-emerald-600">(optional — Served within verified radius)</span>}</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <Button type="button" data-testid="open-camera-button" variant="outline" onClick={openCamera}><Camera className="mr-1.5 h-4 w-4" /> Take live photo</Button>
                    <label data-testid="photo-fallback-label" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500 hover:border-slate-400">
                      Upload from device (not live)
                      <input data-testid="photo-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={uploadPhoto} />
                    </label>
                  </div>
                  {camOpen && (
                    <div className="mt-2 rounded-lg border border-slate-200 bg-black/90 p-2" data-testid="camera-panel">
                      <video ref={videoRef} playsInline muted className="w-full rounded-md" style={{ maxHeight: 260 }} />
                      <div className="mt-2 flex gap-2">
                        <Button type="button" size="sm" data-testid="capture-photo-button" onClick={capturePhoto} className="bg-emerald-600 text-white hover:bg-emerald-700">Capture</Button>
                        <Button type="button" size="sm" variant="outline" data-testid="close-camera-button" onClick={closeCamera}>Close camera</Button>
                      </div>
                    </div>
                  )}
                  {photos.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {photos.map((pid) => (
                        <div key={pid} className="relative h-14 w-14 overflow-hidden rounded-md">
                          <FileImage fileId={pid} className="h-14 w-14 object-cover" />
                          {!livePhotos.includes(pid) && <span data-testid={`photo-notlive-${pid}`} className="absolute bottom-0 left-0 right-0 bg-amber-500 text-center text-[8px] font-bold leading-tight text-white">NOT LIVE</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Textarea data-testid="attempt-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observed a black sedan; no answer at door…" rows={2} />
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Declaration (auto-written)</Label>
                  <p data-testid="attempt-declaration-text" className="mt-1 text-sm text-slate-700">{declarationText}</p>
                  <label className="mt-3 flex items-start gap-2 text-sm font-medium text-slate-800">
                    <input type="checkbox" data-testid="attempt-perjury-confirm" checked={declConfirmed} onChange={(e) => setDeclConfirmed(e.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300" />
                    I declare under penalty of perjury that the foregoing is true and correct.
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="attempt-log-submit-button" onClick={submitAttempt} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800">
                  {busy ? "Saving…" : "Submit attempt"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </>
          )}

          {canAffidavit && !manualAttempt && (
            <Dialog open={affOpen} onOpenChange={setAffOpen}>
              <DialogTrigger asChild>
                <Button data-testid="complete-affidavit-button" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><FileSignature className="mr-1.5 h-4 w-4" /> Complete affidavit</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Affidavit of service</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">Your electronic signature certifies the recorded attempts, GPS coordinates, and photo evidence are true and accurate.</p>
                  <div><Label>Electronic signature (full legal name)</Label><Input data-testid="affidavit-signature-input" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="John Smith" /></div>
                  <div><Label>Additional statement (optional)</Label><Textarea data-testid="affidavit-statement-input" value={statement} onChange={(e) => setStatement(e.target.value)} rows={3} placeholder="Personally served the defendant at the residence…" /></div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input data-testid="print-proof-checkbox" type="checkbox" checked={printRequested} onChange={(e) => setPrintRequested(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                      <FileText className="h-4 w-4 text-slate-400" /> Print completed proof / documentation
                    </label>
                    {printRequested && (
                      <div className="mt-3 flex items-center gap-2">
                        <Label className="text-xs text-slate-500">Pages</Label>
                        <Input data-testid="print-pages-input" type="number" min={1} value={printPages} onChange={(e) => setPrintPages(e.target.value)} className="h-8 w-24" />
                        <span className="text-xs text-slate-400">Print cost is set by ARPS and shown on your payout.</span>
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="submit-affidavit-button" onClick={submitAffidavit} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700">{busy ? "Submitting…" : "Sign & submit"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {manualAffNeeded && (
            <div data-testid="manual-affidavit-panel" className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <p className="font-semibold text-amber-900">Manual, notarized affidavit required</p>
              <p className="mt-1 text-sm text-amber-800">Photo/GPS could not be captured on this served attempt. Download the pre-filled affidavit, sign it in front of a notary, then upload the scan. This attempt stays Unverified until an admin approves the notarized affidavit.</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" data-testid="download-manual-affidavit" onClick={downloadManualAffidavit} className="border-amber-400 text-amber-800 hover:bg-amber-100"><FileSignature className="mr-1.5 h-4 w-4" /> Download pre-filled PDF</Button>
                <label data-testid="manual-scan-label" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-amber-400 px-3 py-2 text-sm text-amber-800 hover:bg-amber-100">
                  {manualScan ? `Scan: ${manualScan.name}` : "Upload notarized scan"}
                  <input data-testid="manual-scan-input" type="file" accept="application/pdf,image/*" className="hidden" onChange={uploadManualScan} />
                </label>
                <Button type="button" data-testid="submit-manual-affidavit" onClick={submitManualAffidavit} disabled={manualBusy || !manualScan} className="bg-amber-600 text-white hover:bg-amber-700">{manualBusy ? "Submitting…" : "Submit for review"}</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {order.affidavit && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {order.affidavit.manual_notarized ? "Manual, notarized affidavit submitted." : `Affidavit signed by ${order.affidavit.signature}.`} {order.status === "completed" ? "Approved by admin." : "Awaiting admin review."}
        </div>
      )}

      {(order.status === "pending_review" || order.status === "completed") && isMine && (
        <ReturnOfService orderId={id} />
      )}

      {/* Server compensation / payout — the authenticated server's OWN payout info only */}
      {order.compensation && (() => {
        const c = order.compensation;
        const cur = c.currency;
        const paid = c.payment_status === "paid";
        const processing = c.payout_state === "processing";
        const scheduled = c.payout_state === "scheduled";
        const settledOrMoving = paid || processing || scheduled;
        const eligible = !!c.payout_eligible && !settledOrMoving;
        const hasMethod = !!(payoutProfile && payoutProfile.verified);
        const statusLabel = paid ? "Paid" : processing ? "Processing (24-Hour)" : scheduled ? "Scheduled (Standard)"
          : eligible ? "Eligible"
          : c.eligibility === "awaiting_admin_seal" ? "Awaiting admin approval"
          : c.eligibility === "awaiting_client_payment" ? "Awaiting client payment" : "Pending";
        const ref = c.external_payment_ref || c.payout_reference;
        const gross = Number(c.net_payout || 0);
        const fee = transferType === "expedited" ? Math.round(gross * 5) / 100 : 0;
        const net = Math.round((gross - fee) * 100) / 100;
        const fmt = (n) => `${cur} ${Number(n).toFixed(2)}`;
        return (
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="server-payout-card">
            <h3 className="flex items-center gap-1.5 font-display text-base font-bold text-slate-900">
              <Wallet className="h-4 w-4 text-emerald-600" /> Your compensation & payout
            </h3>
            <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              <Detail label="Your payout" value={fmt(gross)} testid="payout-amount" />
              <Detail label="Payout status" value={statusLabel} testid="payout-status" />
              <Detail label="Preferred payout method" value={hasMethod ? `${payoutProfile.method.replace("_", " ")} · ${payoutProfile.masked_ref}` : "None on file"} testid="payout-method" />
              {(processing || scheduled || paid) && c.payout_fee != null && <Detail label="Expedited fee" value={fmt(c.payout_fee)} testid="payout-fee" />}
              {(processing || scheduled || paid) && c.payout_net != null && <Detail label="Net transferred" value={fmt(c.payout_net)} testid="payout-net-amount" />}
              {paid && <Detail label="Payout date" value={c.paid_at ? new Date(c.paid_at).toLocaleString() : "—"} testid="payout-date" />}
              {ref && <Detail label="Payout reference" value={ref} testid="payout-reference" />}
            </dl>

            {eligible && !hasMethod && (
              <Button data-testid="add-payout-method-button" onClick={() => { setMethodErr(""); setMethodOpen(true); }} className="mt-4 bg-slate-900 text-white hover:bg-slate-800">
                <CreditCard className="mr-1.5 h-4 w-4" /> Add Payout Method
              </Button>
            )}

            {eligible && hasMethod && (
              <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-700">Choose transfer speed</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <button type="button" data-testid="transfer-type-expedited" onClick={() => setTransferType("expedited")}
                    className={`rounded-lg border p-3 text-left text-sm ${transferType === "expedited" ? "border-emerald-500 bg-white ring-1 ring-emerald-500" : "border-slate-200 bg-white"}`}>
                    <p className="font-semibold text-slate-900">Expedited · 24 hours</p>
                    <p className="text-xs font-bold text-red-600" data-testid="expedited-fee-warning">24-HOUR PAYOUT = 5% EXPEDITED FEE</p>
                    <p className="text-xs text-slate-500">Fee {fmt(Math.round(gross * 5) / 100)} · You receive {fmt(Math.round((gross - Math.round(gross * 5) / 100) * 100) / 100)}</p>
                  </button>
                  <button type="button" data-testid="transfer-type-weekly" onClick={() => setTransferType("standard")}
                    className={`rounded-lg border p-3 text-left text-sm ${transferType !== "expedited" ? "border-emerald-500 bg-white ring-1 ring-emerald-500" : "border-slate-200 bg-white"}`}>
                    <p className="font-semibold text-slate-900">Standard · no fee</p>
                    <p className="text-xs font-medium text-emerald-700">Standard Payout — No Expedited Fee</p>
                    <p className="text-xs text-slate-500">You receive {fmt(gross)} on the standard cycle</p>
                  </button>
                </div>
                <Button data-testid="initiate-payout-button" onClick={() => { setConfirmChecked(false); setAckChecked(false); setSelectedMethodId(payoutProfile?.preferred_method_id || ""); setTransferOpen(true); }} disabled={busy}
                  className="w-full bg-emerald-600 text-white hover:bg-emerald-700">
                  Confirm &amp; Initiate Transfer · {fmt(net)}
                </Button>
              </div>
            )}

            {processing && <p className="mt-3 text-sm font-medium text-amber-700" data-testid="payout-processing-note">Expedited transfer initiated — processing.</p>}
            {scheduled && <p className="mt-3 text-sm font-medium text-sky-700" data-testid="payout-scheduled-note">Scheduled for the next no-fee standard payout cycle.</p>}
            {paid && <p className="mt-3 text-sm font-medium text-emerald-700" data-testid="payout-paid-note">Payout completed.</p>}

            {/* Add payout method dialog */}
            <Dialog open={methodOpen} onOpenChange={setMethodOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Add payout method</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label>Legal / payee name</Label><Input data-testid="payee-name-input" value={payeeName} onChange={(e) => setPayeeName(e.target.value)} placeholder="John Q. Smith" /></div>
                  <div>
                    <Label>Payout method</Label>
                    <Select value={payMethod} onValueChange={setPayMethod}>
                      <SelectTrigger data-testid="payout-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="debit_card">Debit card</SelectItem>
                        <SelectItem value="zelle">Zelle</SelectItem>
                        <SelectItem value="bank_account">Bank account (weekly)</SelectItem>
                        <SelectItem value="paypal">PayPal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{payMethod === "zelle" ? "Zelle email / phone" : payMethod === "debit_card" ? "Debit card number" : payMethod === "paypal" ? "PayPal recipient email" : "Bank account number"}</Label>
                    <Input data-testid="payout-account-input" value={accountRef} onChange={(e) => setAccountRef(e.target.value)} placeholder={payMethod === "zelle" ? "name@email.com" : payMethod === "paypal" ? "recipient@email.com" : "•••• •••• •••• 1234"} />
                    <p className="mt-1 text-xs text-slate-400">Only masked identifiers are stored. Full banking numbers are never saved or displayed.</p>
                  </div>
                  {payMethod === "bank_account" && (
                    <div>
                      <Label>Routing number</Label>
                      <Input data-testid="payout-routing-input" value={bankRouting} onChange={(e) => setBankRouting(e.target.value)} placeholder="9-digit routing number" />
                      <p className="mt-1 text-xs text-slate-400">Required for bank-account payouts. Only a masked identifier is stored.</p>
                    </div>
                  )}
                </div>
                <DialogFooter className="flex-col items-stretch gap-2 sm:flex-col">
                  {methodErr && <p className="text-sm font-medium text-red-600" data-testid="payout-method-error">{methodErr}</p>}
                  <Button data-testid="save-payout-method-button" onClick={savePayoutProfile} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800">{busy ? "Saving…" : "Save method"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Confirm & initiate transfer dialog */}
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Confirm &amp; initiate transfer</DialogTitle></DialogHeader>
                <div className="space-y-2 text-sm" data-testid="transfer-confirm-summary">
                  <div className="flex justify-between"><span className="text-slate-500">Job number</span><span className="font-semibold">#{order.job_number}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Service completed</span><span className="font-semibold">{c.paid_at || order.updated_at ? new Date(c.client_paid_at || order.updated_at).toLocaleDateString() : "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Payout amount</span><span className="font-semibold">{fmt(gross)}</span></div>
                  <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Method</span>
                    {(payoutProfile?.methods || []).length > 1 ? (
                      <Select value={selectedMethodId} onValueChange={setSelectedMethodId}>
                        <SelectTrigger data-testid="confirm-method-select" className="h-8 w-52"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(payoutProfile?.methods || []).map((m) => (
                            <SelectItem key={m.id} value={m.id}>{m.method.replace("_", " ")} · {m.masked_ref}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="font-semibold" data-testid="confirm-method">{hasMethod ? `${payoutProfile.method.replace("_", " ")} · ${payoutProfile.masked_ref}` : "—"}</span>
                    )}
                  </div>
                  <div className="flex justify-between"><span className="text-slate-500">Transfer speed</span><span className="font-semibold">{transferType === "expedited" ? "24-Hour (expedited)" : "Standard (no fee)"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Expedited fee{transferType === "expedited" ? " (5%)" : ""}</span><span className="font-semibold" data-testid="confirm-fee">{fmt(fee)}</span></div>
                  {transferType === "expedited" && (
                    <p className="rounded-md bg-red-50 px-2 py-1 text-center text-xs font-bold text-red-600" data-testid="confirm-fee-warning">24-HOUR PAYOUT = 5% EXPEDITED FEE</p>
                  )}
                  <div className="flex justify-between border-t border-slate-200 pt-2 text-base"><span className="text-slate-700">Net to be transferred</span><span className="font-extrabold text-emerald-600" data-testid="confirm-net">{fmt(net)}</span></div>
                  {transferType === "expedited" && (
                    <label className="mt-2 flex items-start gap-2 text-slate-700">
                      <input data-testid="expedited-ack-checkbox" type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
                      I understand that selecting the 24-Hour payout option deducts a 5% expedited fee from my compensation.
                    </label>
                  )}
                  <label className="mt-2 flex items-center gap-2 text-slate-700">
                    <input data-testid="transfer-confirm-checkbox" type="checkbox" checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                    I confirm I am requesting payment for this job.
                  </label>
                </div>
                <DialogFooter>
                  <Button data-testid="confirm-transfer-button" onClick={confirmTransfer} disabled={busy || !confirmChecked || (transferType === "expedited" && !ackChecked)} className="bg-emerald-600 text-white hover:bg-emerald-700">{busy ? "Processing…" : `Confirm & Process Payout · ${fmt(net)}`}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        );
      })()}

      {/* Attempts */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="mb-4 font-display text-base font-bold text-slate-900">Logged attempts</h3>
        {(order.attempts || []).length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">No attempts logged yet.</p>
        ) : (
          <div className="space-y-3">
            {order.attempts.map((a) => {
              const eligible = attemptNeedsSupplemental(a);
              return (
                <div key={a.id}>
                  <AttemptCard attempt={a} />
                  <SupplementalEvidenceList orderId={id} attemptNumber={a.attempt_number} refreshKey={suppRefresh} />
                  {eligible && isMine && (
                    <div className="mt-2">
                      <Button variant="outline" size="sm" data-testid={`add-supplemental-button-${a.attempt_number}`}
                        onClick={() => openSupp(a)} className="border-amber-300 text-amber-700 hover:bg-amber-50">
                        <PlusCircle className="mr-1.5 h-4 w-4" /> Add supplemental location evidence
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Supplemental location evidence — new, separately timestamped record; never alters the original attempt */}
      <Dialog open={!!suppFor} onOpenChange={(o) => { if (!o) closeSupp(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add supplemental location evidence</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div data-testid="supplemental-warning" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <p className="font-semibold">This does NOT change your original attempt.</p>
              <p className="mt-1">Supplemental evidence is a new record, timestamped now by the system. Your original Attempt #{suppFor?.attempt_number} time, GPS, and photos are permanent and remain unchanged. Capture a current GPS reading and photo at the service location.</p>
            </div>
            <div>
              <Label>GPS location (captured now)</Label>
              <div className="mt-1 flex items-center gap-3">
                <Button type="button" variant="outline" data-testid="supplemental-gps-button" onClick={captureSuppGps} disabled={suppCapturing}>
                  <Crosshair className="mr-1.5 h-4 w-4" /> {suppCapturing ? "Capturing…" : "Capture GPS"}
                </Button>
                {suppGps && <span data-testid="supplemental-gps-value" className="font-mono-arps text-xs text-emerald-700">{suppGps.lat.toFixed(5)}, {suppGps.lng.toFixed(5)}{suppGps.accuracy != null ? ` ±${Math.round(suppGps.accuracy)}m` : ""}</span>}
              </div>
            </div>
            <div>
              <Label>Photo (captured now) <span className="text-red-500">*</span></Label>
              <label data-testid="supplemental-photo-label" className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-600 hover:border-amber-400">
                <Camera className="h-4 w-4" /> {suppPhoto ? "Photo added — tap to replace" : "Take / upload a current photo"}
                <input data-testid="supplemental-photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadSuppPhoto} />
              </label>
              {suppPhoto && <div className="mt-2"><FileImage fileId={suppPhoto} className="h-16 w-16 rounded-md object-cover" alt="Supplemental preview" /></div>}
            </div>
            <div><Label>Note (optional)</Label><Textarea data-testid="supplemental-note-input" value={suppNote} onChange={(e) => setSuppNote(e.target.value)} rows={2} placeholder="Context for this supplemental capture…" /></div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="supplemental-ack-checkbox" checked={suppAck} onChange={(e) => setSuppAck(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
              <span>I acknowledge this is <strong>new evidence captured now</strong> and does not replace or alter my original attempt.</span>
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="submit-supplemental-button" onClick={submitSupplemental} disabled={suppBusy || !suppAck || !suppPhoto} className="bg-amber-600 text-white hover:bg-amber-700">{suppBusy ? "Saving…" : "Add supplemental evidence"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentRequests orderId={id} role="server" orderStatus={order.status} />
      {docsUnlocked ? (
        <DocumentVault orderId={id} role="server" />
      ) : (
        <div data-testid="documents-locked-notice" className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">
          Case documents are locked until you accept this assignment.
        </div>
      )}
    </div>
  );
}
